import React from 'react';
import PropTypes from 'prop-types';
import { Element } from 'react-scroll';
import isEmpty from 'lodash/isEmpty';
import { getKey, isVisible } from '../../utils/MenuUtils';
import MenuItem from '../MenuItem/MenuItem';
import MenuHeader from '../MenuHeader';
import styles from './MenuViewer.scss';
import SectionTitle from '../SectionTitle';
import endOfMenuBG from './menuRepeatSVG.svg';
import EmptyStateHelper from '../EmptyStateHelper';
import { EmptyStates } from '../../utils/constants';
import { MenuTitle } from '../MenuTitle/MenuTitle';

const MARGIN_TOP = 12;
const headerOffset = '130px';
export default class MenuViewer extends React.PureComponent {
  static displayName = 'MenuViewer';

  static propTypes = {
    height: PropTypes.number,
    simplifiedMenu: PropTypes.object,
    locale: PropTypes.string,
    deliveryType: PropTypes.string,
    timezone: PropTypes.string,
    currency: PropTypes.string,
    query: PropTypes.string,
    filters: PropTypes.object,
    onQueryChange: PropTypes.func,
    onFilter: PropTypes.func,
    onClearFilters: PropTypes.func,
    onDishSelect: PropTypes.func,
    isScrolled: PropTypes.bool,
    subRoute: PropTypes.array,
    onScroll: PropTypes.func,
  };

  static defaultProps = {
    onQueryChange: () => {},
    onFilter: () => {},
    onClearFilters: () => {},
    onDishSelect: () => {},
    subRoute: [],
  };

  state = {
    bufferHeight: 0,
    isMenuHeaderFloating: false,
  };

  constructor() {
    super();

    this._getMenu = this._getMenu.bind(this);
    this._getSection = this._getSection.bind(this);
    this._getDish = this._getDish.bind(this);
    this._onQueryChange = this._onQueryChange.bind(this);
    this._onFilter = this._onFilter.bind(this);
    this._onDishSelect = this._onDishSelect.bind(this);
  }

  componentDidMount() {
    this._updateBufferHeight();
  }

  componentDidUpdate() {
    this._updateBufferHeight();
  }

  _handleScroll = e => {
    const { scrollTop } = e.target;

    const isMenuHeaderFloating = scrollTop > 10;

    if (this.state.isMenuHeaderFloating !== isMenuHeaderFloating) {
      this.setState({ isMenuHeaderFloating });
    }

    this.props.onScroll(e);
  };

  render() {
    const { query, filters, onClearFilters, organizationId, showFilters, toggleFilters, searchRef } = this.props;
    const { isMenuHeaderFloating } = this.state;
    const breadcrumbs = this._getBreadCrumbs();
    const menus = this._getMenus();
    return (
      <div className={styles.wrapper} data-hook="order-manager-container">
        <div  className={styles.searchHeader} ref={this._headerRef} data-hook="menu-viewer-header">
          <MenuHeader
            searchRef={searchRef}
            toggleFilters={toggleFilters}
            showFilters={showFilters}
            isFloating={isMenuHeaderFloating}
            query={query}
            filters={filters}
            onClearFilters={onClearFilters}
            onChange={this._onQueryChange}
            onFilter={this._onFilter}
            breadcrumbs={breadcrumbs}
            organizationId={organizationId}
          />
        </div>
        <Element
          onScroll={this._handleScroll}
          className={styles.menus}
          id="scroll-container"
          data-hook="menu-viewer-container"
        >
          {menus}
          <div
            style={{
              height: this.state.bufferHeight,
              backgroundImage: `url(${endOfMenuBG})`,
              backgroundRepeatY: 'no-repeat',
              backgroundPosition: 'bottom',
            }}
          />
        </Element>
      </div>
    );
  }

  _headerRef = el => {
    this._header = el;
  };

  _lastSectionRef = el => {
    this._lastSection = el;
  };

  _getBreadCrumbs() {
    const { subRoute, simplifiedMenu, locale } = this.props;
    const { simplifiedMenus } = simplifiedMenu;
    const breadcrumbs = [];
    const menu = simplifiedMenus.find((m, index) =>
      subRoute.length > 0 ? getKey(m.title[locale]) === subRoute[0] : index === 0,
    );
    const section = menu
      ? menu.sections.find((s, index) => (subRoute.length > 1 ? getKey(s.title[locale]) === subRoute[1] : index === 0))
      : '';

    if (menu) {
      breadcrumbs.push(menu.title[locale]);
    }
    if (section) {
      breadcrumbs.push(section.title[locale]);
    }

    return breadcrumbs;
  }

  _getMenus() {
    const { simplifiedMenu, query } = this.props;
    const { simplifiedMenus } = simplifiedMenu;
    const menus = simplifiedMenus.map(this._getMenu);
    return menus.find(menu => menu !== null) ? (
      menus
    ) : (
      <EmptyStateHelper type={EmptyStates.NO_SEARCH_RESULTS} additionalTitleText={query} />
    );
  }

  _getMenu(simplifiedMenu, id, { length }) {
    const { locale } = this.props;
    const title = simplifiedMenu.title[locale];
    const scrollKey = getKey(title);

    return this._renderItem(simplifiedMenu, id, matchesQuery => {
      const children = simplifiedMenu.sections.reduce((acc, section, index, sectionsArr) => {
        const child = this._getSection(section, index, matchesQuery, title, sectionsArr.length, id === length - 1);
        if (child) {
          acc.push(child);
        }
        return acc;
      }, []);

      return children.length ? (
        <div key={`menu-${id}`}>
          <div
            data-scroll="section-scroll"
            data-menu={'menu'}
            data-name={simplifiedMenu.id}
            style={{ display: 'flex', justifyContent: 'center' }}
          >
            <MenuTitle>{title}</MenuTitle>
          </div>
          <Element key={id} name={simplifiedMenu.id} data-hook="menu-section">
            <div className={`${styles.menu} ${id === length - 1 ? styles.lastMenu : ''}`} id={scrollKey}>
              {children}
            </div>
          </Element>
        </div>
      ) : null;
    });
  }

  _getSection(section, id, forceShow, menuTitle, arrayLength, isLastMenu) {
    const { locale, simplifiedMenu, subRoute } = this.props;
    const { simplifiedItems } = simplifiedMenu;
    const sectionItems = simplifiedItems.filter(item => section.itemIds.indexOf(item.id) > -1);
    const title = section.title[locale];
    const scrollKey = getKey(title);
    const showTitle = subRoute.length > 0 ? subRoute.indexOf(scrollKey) === -1 : id !== 0;
    const labels = [title];
    return this._renderItem(section, id, matchesQuery => {
      const children = sectionItems.reduce((acc, dish, index) => {
        const child = this._getDish(dish, index, matchesQuery || forceShow);
        if (child) {
          acc.push(child);
        }
        return acc;
      }, []);
      return (matchesQuery || forceShow || children.length) && !(!children.length && forceShow) ? (
        <Element key={id} name={section.id} data-hook="section-title" data-scroll="section-scroll">
          <div
            className={`${styles.section} ${id === arrayLength - 1 ? styles.lastSection : ''}`}
            ref={isLastMenu && id === arrayLength - 1 ? this._lastSectionRef : undefined}
          >
            <div className={`${styles.sectionTitle} ${showTitle ? styles.showTitle : ''}`}>
              <SectionTitle labels={labels} />
            </div>
            <div className={styles.items}>{children}</div>
          </div>
        </Element>
      ) : null;
    });
  }

  _getDish(item, id, forceShow) {
    const { locale, simplifiedMenu, currency, deliveryType, menu } = this.props;
    const matchesFilters = this._matchesFilters(item);

    return this._renderItem(item, id, matchesQuery =>
      matchesFilters && (matchesQuery || forceShow) ? (
        <MenuItem
          item={item}
          key={id}
          locale={locale}
          simplifiedMenu={simplifiedMenu}
          currency={currency}
          onClick={this._onDishSelect}
          deliveryType={deliveryType}
        />
      ) : null,
    );
  }

  _renderItem(item, id, render) {
    const { simplifiedMenu, deliveryType, timezone, locale, query } = this.props;
    const title = item.title[locale];
    const matchesQuery = !query || title.toLowerCase().indexOf(query.toLowerCase()) > -1;

    return isVisible(item, deliveryType, timezone, simplifiedMenu.modified) ? render(matchesQuery) : null;
  }

  _matchesFilters(item) {
    const { filters } = this.props;
    let matches = true;

    if (!isEmpty(filters)) {
      const filterList = Object.keys(filters);
      const labels = item.labels || [];

      for (let i = 0; i < filterList.length; i++) {
        matches = matches && labels.indexOf(filterList[i]) > -1;
      }
    }

    return matches;
  }

  _onQueryChange(e) {
    const { onQueryChange } = this.props;
    onQueryChange(e.target.value);
  }

  _onFilter(label) {
    const { onFilter } = this.props;
    onFilter(label);
  }

  _onDishSelect(dishId) {
    const { onDishSelect } = this.props;
    onDishSelect(dishId);
  }

  _updateBufferHeight = () => {
    if (this._lastSection) {
      this.setState({ bufferHeight: `calc(100vh - ${headerOffset} - ${this._lastSection.offsetHeight}px)` });
    }
  };
}
